import { ReimbursementTypes } from '@app/enums/ReimbursementTypes';
import { TravelRequestEnum } from '@app/enums/travel-request.enum';
import { MdsOptionGeneric } from '@utility/utility';
import { Moment, utc } from 'moment';
import { NoteCardElement } from '../v2/components/request-side-notes/note.model';
import { CountryViewModel } from './country';
import { CurrencyViewModel } from './currency';
import { TravelHistoryData } from './history-data/travelHistoryData';
import { Patient } from './patient';
import { VisitName } from './study';
import { TransferData, TransferData_DTO } from './transferData';

export interface RequestNote extends NoteCardElement {}

export class TravelAccommodations {
    needsAirTravel: boolean;
    flightRequests: string;

    flightReceiptDate: Moment;
    flightTransDate: Moment;
    flightMerchant: number;
    flightUsdExpense?: number;
    flightTransAmount: number;
    flightTransCurrency: string;
    flightDoc: File; //base64 binary string (from btoa())
    flightAttachmentInfoId?: number;
    flightRequestDetailsId?: number;
    flightRequestDetails: TravelRequestFlightTravelDetail;

    needsTrainTravel: boolean;

    /**
     * Legacy field for "Special requests" textarea value. Displayed for old requests that don't have data in the structured form.
     * All new Travel Requests store this value in `trainTravelDetailsForPatient.specialRequests`.
     */
    trainRequests: string;
    trainMerchant: number;
    trainUsdExpense?: number;
    trainReceiptDate: Moment;
    trainTransDate: Moment;
    trainCost: number;
    trainCurrency: string;
    trainDoc: File;
    trainAttachmentInfoId?: number;
    trainTravelDetailsForPatient: TrainTravelDetails;

    needsLodging: boolean;
    lodgingCheckInDate: Moment;
    lodgingCheckOutDate: Moment;
    lodgingDateRequired: boolean;
    lodgingRequests: string;

    lodgingReceiptDate: Moment;
    lodgingTransDate: Moment;
    lodgingMerchant: number;
    lodgingUsdExpense?: number;
    lodgingCost: number;
    lodgingCurrency: string;
    lodgingDoc: File;
    lodgingAttachmentInfoId?: number;

    needsCar: boolean;
    carServiceRequests: string;
    groundReceiptDate: Moment;
    groundTransDate: Moment;
    groundMerchant: number;
    groundUsdExpense?: number;
    groundCost: number;
    groundCurrency: string;
    groundDoc: File;
    groundAttachmentInfoId?: number;
    transferDatas: TransferData[];

    needsRentalCar: boolean;
    rentalCarUseFlightDate: boolean | null;
    rentalCarRequests: string;
    rentalCarTransactionDate: Moment;
    rentalCarMerchant: number;
    rentalCarUsdExpense?: number;
    rentalCarDate: Moment;
    rentalCarCost: number;
    rentalCarCurrency: string;
    rentalCarDoc: File;
    rentalCarPickUpTime: string;
    rentalCarDropOffTime: string;
    rentalCarPickUpDate: Moment;
    rentalCarDropOffDate: Moment;
    rentalCarCountry: string;
    rentalCarLocation: string;
    rentalCarAttachmentInfoId?: number;

    needsOther: boolean;
    otherRequests: string;
    otherReceiptDate: Moment;
    otherTransDate: Moment;
    otherMerchant: number;
    otherUsdExpense?: number;
    otherCost: number;
    otherCurrency: string;
    otherDoc: File;
    otherAttachmentInfoId?: number;
}

export interface IRequest {
    type: string;
    visitDetails: VisitDetails;
    status: RequestStatus;
}

export class VisitDetails {
    visitNameId: number;
    visitName: VisitName;
    travelerInstructions: string;
    visitStartDate: Moment;
    visitEndDate: Moment;
    visitStartTime: string;
    visitEndTime: string;
}

export class Expense {
    id: number;
    requestId: number;
    expenseId: number;
    // attachment: string; //this is the base64 string
    attachment: File;
    attachmentId?: number;
    timestamp: string;
    amount: number;
    authorizedAmount: number | null;
    authorizedAmountUsd: number | null;
    authorizedAmountPatientsCurrency: number | null;
    exchangeRateDate?: string | null;
    notes: string;
    currency: string;
    mileageStartLocation: string | undefined;
    mileageEndLocation: string | undefined;
    mileageTotalDistance: number | undefined;
    mileageReimbursementCountry: string | undefined;
    mileageComments: string | undefined;
    mileageReimbursementRatePerUnit: number | undefined;
    mileageDistanceUnits: string | undefined;
    manualTotalCalculation: boolean | undefined;
    isIncomplete: boolean;
    constructor(init?: Partial<Expense>) {
        if (init) Object.assign(this, init);
    }
}
export class Expense_DTO {
    id: number;
    expenseId: number;
    requestId: number;
    category: string;
    attachment: File;
    attachmentId?: number;
    timestamp: string;
    amount: number;
    authorizedAmount: number | null;
    authorizedAmountUsd: number | null;
    authorizedAmountPatientsCurrency: number | null;
    exchangeRateDate?: string | null;
    currency: string;
    mileageStartLocation: string;
    mileageEndLocation: string;
    mileageTotalDistance: number;
    mileageReimbursementCountry: string;
    mileageComments: string;
    mileageReimbursementRatePerUnit: number;
    mileageDistanceUnits: string;
    manualTotalCalculation: boolean;
    isIncomplete: boolean;
    constructor(init?: Partial<Expense_DTO>) {
        if (init) Object.assign(this, init);
    }
}

export class Reimbursement {
    type: ReimbursementTypes;
    transactionDate: string;
    merchant: number;
    submittedTimestamp: Moment;
    totalAmount: number;
    authorizedAmount: number;
    authorizedAmountUsd: number | null;
    authorizedAmountPatientsCurrency: number | null;
    totalAuthorizedAmountUsd: number | null;
    totalAuthorizedAmountPatientsCurrency: number | null;
    deducted?: number;
    deductedUsd?: number;
    deductedCurrency?: string;
    currency: string;
    expenses: Expense[]; //this will map to ICollection<ReimbursementTransactions>
    changedRequestType: boolean;
    notes?: string | null;
    exchangeRateDate?: string | null;
    constructor(init?: Partial<Reimbursement>) {
        if (init) Object.assign(this, init);
    }
}

export enum GenericRequestStatus {
    New = 0,
    Draft = 1,
    Submitted = 2,
    ERSubmitted = 3,
    PendingApproval = 5,
    Denied = 6,
}

export enum TravelRequestStatus {
    New = 0,
    Draft = 1,
    Submitted = 2,
    ERSubmitted = 3,
    BookingInProcess = 4,
    PendingApproval = 5,
    Denied = 6,
    Cancelled = 7,
    PendingInvoice = 8,
    PendingERSubmission = 9,
    PendingSiteVerification = 10,
}

export enum PaymentRequestStatus {
    New = 0,
    Draft = 1,
    Submitted = 2,
    ERSubmitted = 3,
    PaymentInProcess = 4,
    PendingApproval = 5,
    Denied = 6,
    PendingSiteVerification = 7,
    PendingSetUp = 8,
    SetUpFailed = 9,
    PendingPSApproval = 10,
    PendingERSubmission = 11,
    Voided = 12,
    PayoutIssue = 13,
    PQProcessing = 14,
}

export enum RequestStatusNames {
    New = 'New',
    Draft = 'Draft',
    Submitted = 'Submitted',
    Completed = 'Completed',
    InProcess = 'In Process',
    PendingSiteVerification = 'Pending Site Verification',
    ERSubmitted = 'ER Submitted',
    PendingSetUp = 'Pending Set-Up',
    SetUpFailed = 'Set-Up Failed',
    PaymentInProcess = 'Payment In Process',
    PendingPSApproval = 'Pending PS Approval',
    PendingApproval = 'Pending Approval',
    Denied = 'Denied',
    PendingERSubmission = 'Pending ER Submission',
    Voided = 'Voided',
    BookingInProcess = 'Booking In Process',
    Cancelled = 'Cancelled',
    PendingInvoice = 'Pending Invoice',
    PayoutIssue = 'Payout Issue',
    PQProcessing = 'PQ Processing',
}

export interface RequestStatus {
    statusId: number;
    statusName: string;
}

export class GenericRequest implements IRequest {
    id: number;
    requestPseudoId: string;
    type: string; //travel or payment;
    paymentType: string; //stipend or out-of-pocket
    reimbursement: Reimbursement;
    visitDetails: VisitDetails;
    status: RequestStatus;
    patientId: number;
    patient: Patient;
    travelRequests: TravelAccommodations;
    receipts: Receipt[] | null;
    caregiver: boolean;
    isMinor: boolean;
    requestDate: Moment;
    submittingUserId: number;
    submittingUserName: string;
    completingUserId: number;
    completingUserName: number;
    flaggedDuplicate: boolean;
    additionalDetails: string;
    travelRequestCaregivers: TravelRequestCaregiver[];
    erTrackingId: string | null;
    erReportingPeriod: string | null;
    notes?: string | null;
    requestNotes: NoteCardElement[];
}
export interface TravelRequest_DTO {
    id: number;
    requestPseudoId: string;
    status: RequestStatus;
    patientId: number;
    visitNameId: number;
    visitName: VisitName;
    visitStartDate: string;
    visitEndDate: string;
    visitStartTime: string;
    visitEndTime: string;
    caregiver: boolean;
    isMinor: boolean;
    travelerInstructions: string;

    flightRequests: string;
    lodgingRequests: string;
    otherRequests: string;
    carServiceRequests: string;
    trainRequests: string;
    rentalCarRequests: string;

    flightRequired: boolean;
    trainRequired: boolean;
    carServiceRequired: boolean;
    lodgingRequired: boolean;
    otherServiceRequired: boolean;
    rentalCarRequired: boolean;

    flightReceiptDate: string;
    flightTransactionDate: string;
    flightMerchant: number;
    flightTransactionAmount: number;
    flightTransactionCurrency: string;
    flightDoc: File; //base64 binary string (from btoa())
    flightRequestDetailsId?: number;
    flightRequestDetails?: TravelRequestFlightTravelDetail_DTO;

    trainTransactionDate: string;
    trainReceiptDate: string;
    trainMerchant: number;
    trainTransactionAmount: number;
    trainTransactionCurrency: string;
    trainDoc: File;

    // old Travel Requests in the database will have no train travel request objects assigned, so it can be null
    trainTravelDetailsForPatient: TrainTravelDetails_DTO | null;

    carServiceTransactionDate: string;
    carServiceReceiptDate: string;
    carServiceMerchant: number;
    carServiceTransactionAmount: number;
    carServiceTransactionCurrency: string;
    carServiceDoc: File;
    travelRequestTransferData: TransferData_DTO[];

    lodgingCheckInDate: string;
    lodgingCheckOutDate: string;
    lodgingDateRequired: boolean;
    lodgingTransactionDate: string;
    lodgingReceiptDate: string;
    lodgingMerchant: number;
    lodgingTransactionAmount: number;
    lodgingTransactionCurrency: string;
    lodgingDoc: File;

    rentalCarPickUpDate: string;
    rentalCarDropOffDate: string;
    rentalCarUseFlightDate: boolean | null;
    rentalCarPickUpTime: string;
    rentalCarDropOffTime: string;
    rentalCarLocation: string;
    rentalCarCountry: string;
    rentalCarTransactionDate: string;
    rentalCarReceiptDate: string;
    rentalCarMerchant: number;
    rentalCarTransactionAmount: number;
    rentalCarTransactionCurrency: string;
    rentalCarDoc: File;

    otherTransactionDate: string;
    otherReceiptDate: string;
    otherMerchant: number;
    otherTransactionAmount: number;
    otherTransactionCurrency: string;
    otherDoc: File;

    createdTimestamp: string;

    submittingUserId: number;
    submittingUserName: string;
    completingUserId: number;
    completingUserName: number;
    flaggedDuplicate: boolean;
    additionalDetails: string;

    flightUsdExpense?: number;
    trainUsdExpense?: number;
    lodgingUsdExpense?: number;
    carServiceUsdExpense?: number;
    rentalCarUsdExpense?: number;
    otherUsdExpense?: number;

    erTrackingId: string | null;
    erReportingPeriod: string | null;

    travelRequestCaregivers: TravelRequestCaregiver_DTO[];

    receipts: ReceiptDto[] | null;
    travelRequestNotes: RequestNoteDto[];
}

export interface ReceiptDto {
    id: number;
    exchangeRateDate?: string | null;
    travelRequestId: number | null;
    reimbursementRequestId: number | null;
    stipendRequestId: number | null;
    type: number;
    receiptDate: string | null;
    amountLocalCurrency: number | null;
    amountUsd: number | null;
    currencyCode: string | null;
    attachmentInfoId: number | null;
    doc: File | null;
    transactionDate: string | null;
    merchant: number | null;
    erTrackingId: string | null;
    erReportingPeriod: string | null;
    notes?: string | null;
}
export interface Receipt {
    id: number;
    exchangeRateDate?: string | null;
    travelRequestId: number | null;
    reimbursementRequestId: number | null;
    stipendRequestId: number | null;
    type: TravelRequestEnum;
    receiptDate: Moment | null;
    amountLocalCurrency: number | null;
    amountUsd: number | null;
    currencyCode: string | null;
    attachmentInfoId: number | null;
    doc: File | null;
    transactionDate: Moment | null;
    merchant: number | null;
    erTrackingId: string | null;
    erReportingPeriod: string | null;
    notes?: string | null;
}
export interface TravelRequest {
    id: number;
    requestPseudoId: string;
    status: RequestStatus;
    patientId: number;
    visitNameId: number;
    visitName: VisitName;
    visitStartDate: Moment;
    visitEndDate: Moment;
    visitStartTime: string;
    visitEndTime: string;
    caregiver: boolean;
    isMinor: boolean;
    travelerInstructions: string;

    flightRequests: string;
    lodgingRequests: string;
    otherRequests: string;
    carServiceRequests: string;
    trainRequests: string;
    rentalCarRequests: string;

    flightRequired: boolean;
    trainRequired: boolean;
    carServiceRequired: boolean;
    lodgingRequired: boolean;
    otherServiceRequired: boolean;
    rentalCarRequired: boolean;

    receipts: Receipt[] | null;

    flightReceiptDate: Moment;
    flightTransactionDate: Moment;
    flightMerchant: number;
    flightTransactionAmount: number;
    flightTransactionCurrency: string;
    flightDoc: File; //base64 binary string (from btoa())
    flightAttachmentInfoId?: number;
    flightRequestDetailsId?: number;
    flightRequestDetails?: TravelRequestFlightTravelDetail;

    trainReceiptDate: Moment;
    trainTransactionDate: Moment;
    trainMerchant: number;
    trainTransactionAmount: number;
    trainTransactionCurrency: string;
    trainDoc: File;
    trainAttachmentInfoId?: number;
    trainTravelDetailsForPatient: TrainTravelDetails;

    carServiceReceiptDate: Moment;
    carServiceTransactionDate: Moment;
    carServiceMerchant: number;
    carServiceTransactionAmount: number;
    carServiceTransactionCurrency: string;
    carServiceDoc: File;
    carServiceAttachmentInfoId?: number;
    travelRequestTransferData: TransferData[];

    lodgingCheckInDate: Moment;
    lodgingCheckOutDate: Moment;
    lodgingDateRequired: boolean;
    lodgingReceiptDate: Moment;
    lodgingTransactionDate: Moment;
    lodgingMerchant: number;
    lodgingTransactionAmount: number;
    lodgingTransactionCurrency: string;
    lodgingDoc: File;
    lodgingAttachmentInfoId?: number;

    rentalCarPickUpDate: Moment;
    rentalCarDropOffDate: Moment;
    rentalCarUseFlightDate: boolean | null;
    rentalCarPickUpTime: string;
    rentalCarDropOffTime: string;
    rentalCarLocation: string;
    rentalCarCountry: string;
    rentalCarReceiptDate: Moment;
    rentalCarTransactionDate: Moment;
    rentalCarMerchant: number;
    rentalCarTransactionAmount: number;
    rentalCarTransactionCurrency: string;
    rentalCarDoc: File;
    rentalCarAttachmentInfoId?: number;

    otherReceiptDate: Moment;
    otherTransactionDate: Moment;
    otherMerchant: number;
    otherTransactionAmount: number;
    otherTransactionCurrency: string;
    otherDoc: File;
    otherAttachmentInfoId?: number;

    createdTimestamp: Moment;

    submittingUserId: number;
    submittingUserName: string;
    completingUserId: number;
    completingUserName: number;
    flaggedDuplicate: boolean;
    additionalDetails: string;

    flightUsdExpense?: number;
    trainUsdExpense?: number;
    lodgingUsdExpense?: number;
    carServiceUsdExpense?: number;
    rentalCarUsdExpense?: number;
    otherUsdExpense?: number;

    erTrackingId: string | null;
    erReportingPeriod: string | null;

    travelRequestCaregivers: TravelRequestCaregiver[];
    notes: NoteCardElement[];
}

export interface ReimbursementRequest_DTO {
    id: number;
    requestPseudoId: string;
    patientId: number;
    visitNameId: number;
    status: RequestStatus;
    visitStartDate: string;
    apptTime: string;
    apptDurationHours: string;
    totalAmount: number;
    totalAmountAuthorized: number;
    totalAmountAuthorizedUsd?: number;
    totalAmountAuthorizedPatientsCurrency?: number;
    deducted?: number;
    deductedUsd?: number;
    deductedCurrency?: string;
    totalCurrency: string;
    transactionDate: string;
    merchant: number | null;
    submittedTimestamp: string;
    reimbursementTransactions: Expense_DTO[];
    travelerInstructions: string;
    createdTimestamp: string;
    submittingUserId: number;
    submittingUserName: string;
    completingUserId: number;
    completingUserName: number;
    flaggedDuplicate: boolean;
    additionalDetails: string;
    changedRequestType: boolean;
    erTrackingId: string | null;
    erReportingPeriod: string | null;
    reimbursementRequestNotes: RequestNoteDto[];
}

export interface RequestNoteDto {
    id: number;
    note: string;
    modifiedInitials: string;
    modifiedTime: string;
}

export interface ReimbursementRequest {
    id: number;
    requestPseudoId: string;
    patientId: number;
    visitNameId: number;
    status: RequestStatus;
    visitStartDate: Moment;
    apptTime: string;
    apptDurationHours: string;
    totalAmount: number;
    totalAmountAuthorized: number;
    totalAmountAuthorizedUsd?: number;
    totalAmountAuthorizedPatientsCurrency?: number;
    deducted?: number;
    deductedUsd?: number;
    deductedCurrency?: string;
    totalCurrency: string;
    transactionDate: string;
    merchant: number | null;
    submittedTimestamp: Moment;
    reimbursementTransactions: Expense[];
    travelerInstructions: string;
    createdTimestamp: Moment;
    submittingUserId: number;
    submittingUserName: string;
    completingUserId: number;
    completingUserName: number;
    flaggedDuplicate: boolean;
    additionalDetails: string;
    changedRequestType: boolean;
    erTrackingId: string | null;
    erReportingPeriod: string | null;
    notes: NoteCardElement[];
}

export interface StipendRequest_DTO {
    id: number;
    requestPseudoId: string;
    patientId: number;
    visitNameId: number;
    status: RequestStatus;
    visitStartDate: string;
    apptTime: string;
    apptDurationHours: string;
    amount: number;
    authorizedAmount: number;
    authorizedAmountUsd: number | null;
    authorizedAmountPatientsCurrency: number | null;
    deducted?: number;
    deductedUsd?: number;
    deductedCurrency?: string;
    currency: string;
    transactionDate: string;
    submittedTimestamp: string;
    travelerInstructions: string;
    createdTimestamp: string;
    liaisonInitials: string;
    submittingUserId: number;
    submittingUserName: string;
    completingUserId: number;
    completingUserName: number;
    flaggedDuplicate: boolean;
    additionalDetails: string;
    changedRequestType: boolean;
    merchant: number;
    erTrackingId: string | null;
    erReportingPeriod: string | null;
    notes?: string | null;
    stipendRequestNotes: RequestNoteDto[];
    exchangeRateDate?: string | null;
}
export interface StipendRequest {
    id: number;
    exchangeRateDate?: string | null;
    requestPseudoId: string;
    patientId: number;
    visitNameId: number;
    status: RequestStatus;
    visitStartDate: Moment;
    apptTime: string;
    apptDurationHours: string;
    amount: number;
    authorizedAmount: number;
    authorizedAmountUsd: number | null;
    authorizedAmountPatientsCurrency: number | null;
    deducted?: number;
    deductedUsd?: number;
    deductedCurrency?: string;
    currency: string;
    transactionDate: string;
    merchant: number | null;
    submittedTimestamp: Moment;
    travelerInstructions: string;
    createdTimestamp: Moment;
    submittingUserId: number;
    submittingUserName: string;
    completingUserId: number;
    completingUserName: number;
    flaggedDuplicate: boolean;
    additionalDetails: string;
    changedRequestType: boolean;
    erTrackingId: string | null;
    erReportingPeriod: string | null;
    notes?: string | null;
    stipendRequestNotes: NoteCardElement[];
}

export interface RequestLog {
    id: number;
    requestId: number;
    statusName: string;
    timestamp: Date;
    details: string;
    responsibleUserName: string;
    responsibleUserEmail: string;
    actionWasPerformedInPatientPortal: boolean;
    anonymized: boolean;
}
export interface RequestLogView {
    id: number;
    statusName: string;
    timestamp: Date;
    details: string;
    responsibleUserName: string;
    responsibleUserEmail: string;
    actionWasPerformedInPatientPortal: boolean;
    anonymized: boolean;
}

export interface RequestStatusDTO {
    requestType: string;
    requestId: number;
    patientId: number;
    additionalDetails: string;
    newStatus: string;
    status: RequestStatus;
    deducted?: number;
    deductedUsd?: number;
    authorizedAmount?: number;
    deductedCurrency?: string;
}

export class MileageReimbursementRate {
    id: string = '';
    countryCode: string = '';
    currencyCode: string = '';
    effectiveDate: Date = null;
    mileageDistanceUnits: string = '';
    mileageReimbursementRatePerUnit: number = 0;
    constructor(init?: Partial<MileageReimbursementRate>) {
        if (init) Object.assign(this, init);
    }
}

export class MileageReimbursementRateData {
    countryCode: string = '';
    currencyCode: string = '';
    effectiveDate: Moment = null;
    mileageDistanceUnits: string = '';
    mileageReimbursementRatePerUnit: number = 0;
}

export interface TravelRequestCaregiver {
    travelRequestId?: number;
    caregiverId: number;
    caregiverName: string;
    isPrimary: boolean;
    isChecked: boolean;

    trainTravelDetails: TrainTravelDetails | null;
    flightRequestDetailsId?: number;
    flightRequestDetails?: TravelRequestFlightTravelDetail;
    flightRequestTheSameAsPatient: boolean;
}

export interface TravelRequestCaregiver_DTO {
    travelRequestId?: number;
    caregiverId: number;
    caregiverName: string;
    isPrimary: boolean;
    isChecked: boolean;

    flightRequestDetailsId?: number;
    flightRequestDetails?: TravelRequestFlightTravelDetail_DTO;
    flightRequestTheSameAsPatient: boolean;

    trainTravelDetails: TrainTravelDetails_DTO | null;
}

export enum TrainTravelType {
    OneWay,
    RoundTrip,
    NoTravel,
}

export interface TrainTravelDetails {
    trainFormInLegacyMode: boolean;

    isSameAsPatient: boolean;
    trainTravelType: TrainTravelType;

    trainStationOrigin: string;
    trainStationDestination: string;
    departureDate: Moment;
    returnDate: Moment | null;
    specialRequests: string;
}

// model of an event sent through EventEmitter when something in the travel plan changes
export interface TrainTravelDetailsFormModel {
    /* Legacy mode means that the train travel request was created when only a single textarea, `trainSpecialRequests`, was available in the form.
       In this mode, we don't display new fields like "Train Station" or "Departure Date", so users can edit old requests without being bothered by
       the validation of new required fields.
    */
    trainFormInLegacyMode: boolean;

    caregiverId: number;

    useIdenticalTravelPlanAsForPatient: boolean;
    trainTravelType: TrainTravelType;

    trainDepartureDate: Moment;
    trainReturnDate: Moment;
    trainSpecialRequests: string;
    trainStationDestination: string;
    trainStationOrigin: string;
}

export interface TravelRequestFlightTravelDetail {
    id: number;
    travelType: number;
    airportOrigin: string;
    airportDestination: string;
    departureDate: Moment;
    returnDate: Moment;
    specialRequests: string;
}

export interface TravelRequestFlightTravelDetail_DTO {
    id: number;
    travelType: number;
    airportOrigin: string;
    airportDestination: string;
    departureDate: string;
    returnDate: string;
    specialRequests: string;
}
export function mapTravelRequestFlightTravelDetail_DTO_To_TravelRequestFlightTravelDetail(
    dto: TravelRequestFlightTravelDetail_DTO
): TravelRequestFlightTravelDetail {
    return <TravelRequestFlightTravelDetail>{
        ...dto,
        departureDate: !!dto.departureDate ? utc(dto.departureDate) : undefined,
        returnDate: !!dto.returnDate ? utc(dto.returnDate) : undefined,
    };
}

export interface TrainTravelDetails_DTO {
    trainTravelType: TrainTravelType;
    trainStationOrigin: string;
    trainStationDestination: string;
    departureDate: string;
    returnDate: string;
    specialRequests: string;
}

export interface RequestSummary {
    id: number;
    flaggedDuplicate: boolean;
    patientId?: number;
    pcsId?: string;
    requestPseudoId?: string;
    isPatientDeleted: boolean;
    patientName?: string;
    patientNum?: string;
    protocol?: string;
    readyForPayment: string;
    readyForPaymentDb?: boolean;
    requestDate: string;
    requestId?: number;
    requestType: string;
    siteId?: number;
    status: string;
    studyId?: number;
    visitDate: string;
    visitName: string;
    type?: string;
}

export interface RequestSummaryViewModel {
    id: number;
    flaggedDuplicate: boolean;
    patientId?: number;
    pcsId?: string;
    isPatientDeleted: boolean;
    patientName?: string;
    patientNum?: string;
    protocol?: string;
    readyForPayment: string;
    readyForPaymentDb?: boolean;
    requestDate: string;
    requestId?: string;
    requestType: string;
    siteId?: number;
    status: string;
    studyId?: number;
    visitDate: string;
    visitName: string;
    type?: string;
}

export function mapTrainTravelDetails_DTO_To_TrainTravelDetails(dto: TrainTravelDetails_DTO): TrainTravelDetails {
    if (!dto) return null;

    return <TrainTravelDetails>{
        ...dto,
        departureDate: utc(dto.departureDate),
        returnDate: dto.returnDate ? utc(dto.returnDate) : null,
    };
}
export function mapTrainTravelDetails_To_TrainTravelDetailsDTO(value: TrainTravelDetails): TrainTravelDetails_DTO {
    if (!value) return null;

    return <TrainTravelDetails_DTO>{
        ...value,
        departureDate: value.departureDate.toISOString(),
        returnDate: value.returnDate ? value.returnDate.toISOString() : null,
    };
}

export function mapTravelRequestCaregiver_DTO_To_TravelRequestCaregivers(
    dto: TravelRequestCaregiver_DTO
): TravelRequestCaregiver {
    return <TravelRequestCaregiver>{
        ...dto,
        trainTravelDetails: mapTrainTravelDetails_DTO_To_TrainTravelDetails(dto.trainTravelDetails),
        flightRequestDetails: dto.flightRequestDetails
            ? mapTravelRequestFlightTravelDetail_DTO_To_TravelRequestFlightTravelDetail(dto.flightRequestDetails)
            : null,
    };
}

export function mapTrainTravelFormModelToTrainTravelDetails(
    formModel: TrainTravelDetailsFormModel
): TrainTravelDetails {
    return {
        isSameAsPatient: formModel.useIdenticalTravelPlanAsForPatient,
        trainTravelType: formModel.trainTravelType,
        trainStationOrigin: formModel.trainStationOrigin,
        trainStationDestination: formModel.trainStationDestination,
        departureDate: formModel.trainDepartureDate,
        returnDate: formModel.trainReturnDate,
        specialRequests: formModel.trainSpecialRequests,
        trainFormInLegacyMode: formModel.trainFormInLegacyMode,
    };
}

export interface TravelRequestTypeCount {
    carServiceCount: number;
    flightCount: number;
    lodgingCount: number;
    otherServiceCount: number;
    rentalCarCount: number;
    trainCount: number;
}

export interface BaseRequest {
    id: number;
    visitNameId: number;
    statusId: number;
    visitStartDate: string;
}

export interface BaseTravelRequest extends BaseRequest {
    visitStartTime: string;
    visitEndDate: string;
    visitEndTime: string;
}

export type TravelCardViewModel = {
    id: number;
    isUpdate: boolean;
    patientBookable: boolean;
    historyData: TravelHistoryData;
    countryOptions: MdsOptionGeneric<CountryViewModel>[];
    currencyOptions: MdsOptionGeneric<CurrencyViewModel>[];
    timeOptions: MdsOptionGeneric<number>[];
};
